import {
  CloudUpload as UploadIcon,
  SwapHoriz as SwapHorizIcon,
  PictureInPictureAlt as PipIcon,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Stack,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Tooltip,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { doc, getDoc } from "firebase/firestore";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  uploadUserFitImage,
  getUserFitImage,
  UserFitImage,
  checkFitWithAI,
  loadRecentFitsWithImages,
} from "./fit-check.service";
import PictureInPictureWindow from "../../components/PictureInPictureWindow";
import { useAuth } from "../../contexts/AuthContext";
import { useRevenueCat } from "../../contexts/RevenueCatContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { db } from "../../firebase/config";
import { usePictureInPicture } from "../../hooks/usePictureInPicture";
import { logger } from "../../services";
import { RecentFits, UserProtectedData } from "../../types";

const FitCheckHome: React.FC = () => {
  const { isPlusPlan } = useRevenueCat();
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSupported: pipSupported, isOpen: pipOpen, openPiP, updatePiP } = usePictureInPicture(theme);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [dragOver, setDragOver] = useState(false);
  const [userFitImage, setUserFitImage] = useState<UserFitImage | null>(null);
  const [shopLink, setShopLink] = useState<string>("");
  const [analyzing, setAnalyzing] = useState(false);
  const [recentFits, setRecentFits] = useState<RecentFits[]>([]);
  const [latestFitImage, setLatestFitImage] = useState<string | null>(null);
  const [loadingRecentFits, setLoadingRecentFits] = useState(false);
  const [fitImages, setFitImages] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dailyTrialCount, setDailyTrialCount] = useState<number | null>(null);
  const [pipError, setPipError] = useState<string | null>(null);
  const DAILY_TRIAL_LIMIT = 3;

  // Load user's protected trial info
  const loadTrialInfo = useCallback(async () => {
    if (!user?.uid) {
      return;
    }
    try {
      // Preferred location (new): /users/{uid}/protected/protected
      const protectedRef = doc(db, "users", user.uid, "protected", "protected");
      let snap = await getDoc(protectedRef);

      // Backward compatibility: check old doc id "trials" if the new one doesn't exist
      if (!snap.exists()) {
        const legacyRef = doc(db, "users", user.uid, "protected", "trials");
        snap = await getDoc(legacyRef);
      }

      if (snap.exists()) {
        const data = snap.data() as Partial<UserProtectedData> & { dailyTrialCount?: number };
        const count = typeof data.dailyTrialCount === "number" ? data.dailyTrialCount : 0;
        setDailyTrialCount(count);
      } else {
        setDailyTrialCount(0);
      }
    } catch (e) {
      await logger.error("trial_info_load_failed", e as Error);
      setDailyTrialCount(null);
    }
  }, [user?.uid]);

  // Function to load recent fits for the user
  const loadRecentFits = useCallback(async () => {
    if (!user?.uid) {
      return;
    }

    try {
      setLoadingRecentFits(true);

      const result = await loadRecentFitsWithImages(user.uid, 5);

      setRecentFits(result.fits);
      setFitImages(result.fitImages);
      setLatestFitImage(result.latestFitImage);
    } catch (error) {
      await logger.error("recent_fits_load_failed", error as Error);
      setRecentFits([]);
      setLatestFitImage(null);
      setFitImages({});
    } finally {
      setLoadingRecentFits(false);
    }
  }, [user?.uid]);

  const handleAnalyze = useCallback(async () => {
    if (!selectedImage || !userFitImage || !user?.uid || !shopLink.trim()) {
      const errorMsg = "Please upload your photo and enter a shop link";
      showSnackbar({
        message: errorMsg,
        severity: "error",
        actionLabel: "OK",
      });
      setPipError(errorMsg);
      return;
    }

    // Basic URL validation
    try {
      new URL(shopLink);
    } catch {
      const errorMsg = "Please enter a valid shop URL";
      showSnackbar({
        message: errorMsg,
        severity: "error",
        actionLabel: "OK",
      });
      setPipError(errorMsg);
      return;
    }

    try {
      setAnalyzing(true);
      setPipError(null); // Clear any previous errors

      // Log fit check button click
      await logger.userAction("fit_check_button_clicked", {
        has_plus_plan: isPlusPlan,
        daily_trial_count: dailyTrialCount ?? 0,
        shop_link_domain: new URL(shopLink).hostname,
      });

      // Get the user's ID token for authentication
      const idToken = await user.getIdToken();

      // Call the new backend API for fit checking
      const result = await checkFitWithAI(
        {
          shopLink: shopLink.trim(),
          originalClothingImageUrl: shopLink.trim(),
        },
        idToken,
      );

      if (result.success) {
        // Image has been saved to storage successfully
        showSnackbar({
          message: "Fit visualization generated and saved successfully!",
          severity: "success",
          actionLabel: "Nice!",
        });

        // Reload recent fits to show the newly generated image
        await loadRecentFits();
        // Reload trial info after backend increments counter
        await loadTrialInfo();

        // Clear the shop link for next generation
        setShopLink("");
      } else {
        // Show the specific error message from API response
        const errorMessage = result.error || result.message || "Failed to generate fit visualization";
        throw new Error(errorMessage);
      }
    } catch (error) {
      await logger.error("fit_visualization_failed", error as Error, { shop_link: shopLink });
      const errorMsg = error instanceof Error ? error.message : "Failed to generate fit visualization. Please try again.";
      showSnackbar({
        message: errorMsg,
        severity: "error",
        actionLabel: "OK",
      });
      setPipError(errorMsg);
    } finally {
      setAnalyzing(false);
    }
  }, [selectedImage, userFitImage, user, shopLink, showSnackbar, loadRecentFits, loadTrialInfo, isPlusPlan, dailyTrialCount]);

  const handleUpgradeClick = useCallback(async () => {
    await logger.userAction("upgrade_click", {
      source: "fit_check_trial_alert",
      remaining_trials: Math.max(DAILY_TRIAL_LIMIT - (dailyTrialCount ?? 0), 0),
    });
    navigate("/subscription");
  }, [navigate, dailyTrialCount]);

  const handleFitClick = useCallback((fitId: string) => {
    const fitImageUrl = fitImages[fitId];
    if (fitImageUrl) {
      setLatestFitImage(fitImageUrl);
    }
  }, [fitImages]);

  const handlePipErrorClose = useCallback(() => {
    setPipError(null);
  }, []);

  // Memoized PiP content that updates when dependencies change
  const pipContent = React.useMemo(() => {
    const currentImage = latestFitImage || selectedImage;

    // Transform recent fits data for PiP component
    const pipRecentFits = recentFits.slice(0, 3).map((fit, index) => ({
      id: fit.id,
      imageUrl: fitImages[fit.id],
      title: `Fit #${recentFits.length - index}`,
      subtitle: fit.shopLink.length > 20 ? fit.shopLink.substring(0, 20) + "..." : fit.shopLink,
    }));

    return (
      <PictureInPictureWindow
        imageUrl={currentImage}
        shopLink={shopLink}
        onShopLinkChange={setShopLink}
        onFitCheck={() => handleAnalyze()}
        analyzing={analyzing}
        disabled={uploading || !currentImage}
        recentFits={pipRecentFits}
        onFitClick={handleFitClick}
        error={pipError}
        onErrorClose={handlePipErrorClose}
      />
    );
  }, [
    latestFitImage,
    selectedImage,
    shopLink,
    analyzing,
    uploading,
    handleAnalyze,
    recentFits,
    fitImages,
    handleFitClick,
    pipError,
    handlePipErrorClose,
  ]);

  // Update PiP window content when state changes
  useEffect(() => {
    if (pipOpen) {
      updatePiP(pipContent);
    }
  }, [pipOpen, pipContent, updatePiP]);

  // Load user's existing fit image and recent fits on mount
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) {
        return;
      }

      try {
        setLoading(true);

        // Load user's fit image and recent fits in parallel
        const [existingImage] = await Promise.all([
          getUserFitImage(user.uid),
          loadRecentFits(),
        ]);

        if (existingImage) {
          setUserFitImage(existingImage);
          setSelectedImage(existingImage.url);
        }

        // Load trial info after parallel tasks
        await loadTrialInfo();
      } catch (error) {
        await logger.error("user_data_load_failed", error as Error);
        // Don't show error to user - it's normal to not have an image
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.uid, loadRecentFits, loadTrialInfo]);

  const handleFileSelect = async (file: File) => {
    if (!user?.uid) {
      showSnackbar({
        message: "Please sign in to upload images",
        severity: "error",
        actionLabel: "OK",
      });
      return;
    }

    if (!file.type.startsWith("image/")) {
      showSnackbar({
        message: "Please select an image file",
        severity: "error",
        actionLabel: "OK",
      });
      return;
    }

    // For free users, limit file size to 5MB
    const maxSize = isPlusPlan ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for Plus, 5MB for free
    if (file.size > maxSize) {
      const maxSizeMB = isPlusPlan ? "50MB" : "5MB";
      showSnackbar({
        message: `File size too large. Maximum size is ${maxSizeMB}${!isPlusPlan ? " (upgrade to Plus for 50MB)" : ""}`,
        severity: "error",
        actionLabel: "OK",
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload to Firebase Storage
      const uploadedImage = await uploadUserFitImage(
        user.uid,
        file,
        (progress) => {
          setUploadProgress(progress);
        },
      );

      // Update state with the uploaded image
      setUserFitImage(uploadedImage);
      setSelectedImage(uploadedImage.url);

      await logger.userAction("image_uploaded", { file_size: file.size, file_type: file.type });
      showSnackbar({
        message: "Image uploaded successfully!",
        severity: "success",
        actionLabel: "OK",
      });
    } catch (error) {
      await logger.error("image_upload_failed", error as Error, { file_size: file.size, file_type: file.type });
      showSnackbar({
        message: error instanceof Error ? error.message : "Failed to upload image",
        severity: "error",
        actionLabel: "OK",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    setUserFitImage(null);
    setLatestFitImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenPiP = async () => {
    if (!pipSupported) {
      showSnackbar({
        message: "Picture-in-Picture is not supported in this browser. Try Chrome 116+",
        severity: "error",
        actionLabel: "OK",
      });
      return;
    }

    await openPiP(pipContent, { width: 320, height: 650 });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: 2 }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Try It On!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Click the floating window icon to open the FitCheckr in a floating window. And continue to shop seamlessly.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 2, minHeight: "60vh", justifyContent: "center" }}>
        {/* Main Upload Area */}
        <Box sx={{ width: { xs: "100%", lg: 450 }, flexShrink: 0 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {loading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress size={64} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading your fit image...
                  </Typography>
                </Box>
              ) : uploading ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress size={64} sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Uploading your image...
                  </Typography>
                  {uploadProgress > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(uploadProgress)}%
                    </Typography>
                  )}
                </Box>
              ) : !selectedImage ? (
                <Paper
                  variant="outlined"
                  onDrop={uploading ? undefined : handleDrop}
                  onDragOver={uploading ? undefined : handleDragOver}
                  onDragLeave={uploading ? undefined : handleDragLeave}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                    cursor: uploading ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    bgcolor: dragOver && !uploading ? "action.hover" : "transparent",
                    border: dragOver && !uploading ? "2px dashed primary.main" : "2px dashed grey.300",
                    opacity: uploading ? 0.7 : 1,
                    "&:hover": {
                      bgcolor: uploading ? "transparent" : "action.hover",
                      borderColor: uploading ? "grey.300" : "primary.main",
                    },
                  }}
                  onClick={uploading ? undefined : handleUploadClick}
                >
                  <UploadIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Drop your full body photo here
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    or click to browse files
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1, fontStyle: "italic" }}>
                    Make sure your whole body is visible for best results
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Supports JPG, PNG, WebP • Max {isPlusPlan ? "50MB" : "5MB"}
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ flex: 1, position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Box
                    component="img"
                    src={latestFitImage || selectedImage}
                    alt={latestFitImage ? "Latest AI Generated Fit" : "Your Photo"}
                    sx={{
                      maxWidth: "400px",
                      maxHeight: "65vh",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: 1.5,
                    }}
                  />
                  <Tooltip title="Change image" arrow>
                    <IconButton
                      onClick={handleClearImage}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "background.paper",
                        color: "text.primary",
                        border: "2px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                        boxShadow: 1,
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                          animation: "none",
                        },
                      }}
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Picture-in-Picture Button */}
                  {pipSupported && (
                    <Tooltip title="Open in floating window" arrow>
                      <IconButton
                        onClick={handleOpenPiP}
                        disabled={!selectedImage}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 56,
                          bgcolor: "background.paper",
                          color: "primary.main",
                          border: "2px solid",
                          borderColor: "primary.main",
                          borderRadius: 1,
                          boxShadow: 2,
                          animation: selectedImage && !pipOpen ? "pulse 2s ease-in-out infinite" : "none",
                          "@keyframes pulse": {
                            "0%": {
                              boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.7)",
                              transform: "scale(1)",
                            },
                            "50%": {
                              boxShadow: "0 0 0 8px rgba(25, 118, 210, 0)",
                              transform: "scale(1.05)",
                            },
                            "100%": {
                              boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)",
                              transform: "scale(1)",
                            },
                          },
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            borderColor: "primary.main",
                            animation: "none",
                          },
                          "&:disabled": {
                            bgcolor: "action.disabledBackground",
                            color: "text.disabled",
                            borderColor: "divider",
                            animation: "none",
                          },
                        }}
                      >
                        <PipIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />

              {selectedImage && uploadProgress > 0 && uploadProgress < 100 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Uploading: {Math.round(uploadProgress)}%
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "grey.200",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${uploadProgress}%`,
                        height: "100%",
                        bgcolor: "primary.main",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Mobile Controls - Below Image */}
          {selectedImage && (
            <Card sx={{ mt: 2, display: { xs: "block", lg: "none" } }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Image Address"
                    placeholder="Enter the URL of the clothing item you want to try on"
                    value={shopLink}
                    onChange={(e) => setShopLink(e.target.value)}
                    variant="outlined"
                    disabled={analyzing || uploadProgress > 0}
                    size="small"
                  />

                  <Button
                    variant="contained"
                    onClick={handleAnalyze}
                    disabled={uploading || uploadProgress > 0 || analyzing || !shopLink.trim()}
                    fullWidth
                    sx={{
                      height: "40px",
                    }}
                    startIcon={(uploading || analyzing) ? <CircularProgress size={16} /> : undefined}
                  >
                    {analyzing ? "Generating..." : "Fit Check!"}
                  </Button>

                  {!isPlusPlan && (
                    <Alert severity="warning" sx={{ fontSize: "0.75rem", p: 1 }}>
                      You can only generate 3 fits for free every day.
                      {true && (
                        <> Remaining today: {Math.max(DAILY_TRIAL_LIMIT - (dailyTrialCount ?? 0), 0)} / {DAILY_TRIAL_LIMIT}. </>
                      )}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleUpgradeClick}
                        sx={{
                          fontSize: "inherit",
                          fontWeight: "bold",
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "none",
                          },
                        }}
                      >
                        Upgrade to Plus for unlimited fits!
                      </Link>
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Side Panel - Recent Fits and Controls */}
        <Box sx={{ width: 300, display: { xs: "none", lg: "block" } }}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Recent Fits Section */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, mb: selectedImage ? 3 : 0 }}>
                <Typography variant="h6" gutterBottom>
                  Latest Fits ({recentFits.length})
                </Typography>

                {loadingRecentFits ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : recentFits.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No generated fits yet. Create your first AI fit visualization!
                  </Typography>
                ) : (
                  <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
                    <Stack spacing={2}>
                      {recentFits.map((fit, index) => (
                        <Paper
                          key={fit.id}
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            bgcolor: "transparent",
                            borderColor: "grey.300",
                            cursor: fitImages[fit.id] ? "pointer" : "default",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: fitImages[fit.id] ? "action.hover" : "transparent",
                              transform: fitImages[fit.id] ? "translateY(-1px)" : "none",
                            },
                          }}
                          onClick={() => fitImages[fit.id] && handleFitClick(fit.id)}
                        >
                          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                            {fitImages[fit.id] ? (
                              <Box
                                component="img"
                                src={fitImages[fit.id]}
                                alt={`AI Fit #${recentFits.length - index}`}
                                sx={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "grey.300",
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "60px",
                                  height: "60px",
                                  bgcolor: "grey.200",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <CircularProgress size={20} />
                              </Box>
                            )}

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="normal">
                                Fit #{recentFits.length - index}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fit.createdAt.toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Controls Section - Now at bottom */}
              {selectedImage && (
                <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 3 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Image Address"
                      placeholder="Enter the URL of the clothing item you want to try on"
                      value={shopLink}
                      onChange={(e) => setShopLink(e.target.value)}
                      variant="outlined"
                      disabled={analyzing || uploadProgress > 0}
                      size="small"
                    />

                    <Button
                      variant="contained"
                      onClick={handleAnalyze}
                      disabled={uploading || uploadProgress > 0 || analyzing || !shopLink.trim()}
                      fullWidth
                      sx={{
                        height: "40px",
                      }}
                      startIcon={(uploading || analyzing) ? <CircularProgress size={16} /> : undefined}
                    >
                      {analyzing ? "Generating..." : "Fit Check!"}
                    </Button>
                  </Box>

                  {!isPlusPlan && (
                    <Alert severity="warning" sx={{ fontSize: "0.75rem", p: 1, mt: 2 }}>
                      You can only generate 3 fits for free every day.
                      {true && (
                        <> Remaining today: {Math.max(DAILY_TRIAL_LIMIT - (dailyTrialCount ?? 0), 0)} / {DAILY_TRIAL_LIMIT}. </>
                      )}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleUpgradeClick}
                        sx={{
                          fontSize: "inherit",
                          fontWeight: "bold",
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "none",
                          },
                        }}
                      >
                        Upgrade to Plus for unlimited fits!
                      </Link>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Instructions Panel - Step Images - Desktop */}
        <Box sx={{
          width: 500,
          display: { xs: "none", xl: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
          p: 2,
          minHeight: "60vh",
        }}>
          {/* Step 1 */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
              Step 1: Right click on the image and select "Copy Image Address".
            </Typography>
            <Box
              component="img"
              src="/assets/step1.png"
              alt="Step 1 - Upload your photo"
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            />
          </Box>

          {/* Step 2 */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
              Step 2: Paste the image address into the "Image Address" field.
            </Typography>
            <Box
              component="img"
              src={theme.palette.mode === "light" ? "/assets/step2-light.png" : "/assets/step2-dark.png"}
              alt="Step 2 - Enter clothing URL"
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            />
          </Box>

          {/* Step 3 */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
              Step 3: Click the "Fit Check!" button.
            </Typography>
            <Box
              component="img"
              src={theme.palette.mode === "light" ? "/assets/step3-light.png" : "/assets/step3-dark.png"}
              alt="Step 2 - Enter clothing URL"
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Instructions Panel - Mobile - At Bottom */}
      <Box sx={{
        display: { xs: "flex", xl: "none" },
        flexDirection: "column",
        gap: 3,
        mt: 4,
        px: 2,
      }}>
        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center" }}>
          How to Use FitCheckr
        </Typography>

        {/* Step 1 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
            Step 1: Right click on the image and select "Copy Image Address".
          </Typography>
          <Box
            component="img"
            src="/assets/step1.png"
            alt="Step 1 - Copy image address"
            sx={{
              width: "100%",
              maxWidth: "500px",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.300",
            }}
          />
        </Box>

        {/* Step 2 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
            Step 2: Paste the image address into the "Image Address" field.
          </Typography>
          <Box
            component="img"
            src={theme.palette.mode === "light" ? "/assets/step2-light.png" : "/assets/step2-dark.png"}
            alt="Step 2 - Enter clothing URL"
            sx={{
              width: "100%",
              maxWidth: "500px",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.300",
            }}
          />
        </Box>

        {/* Step 3 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, textAlign: "center" }}>
            Step 3: Click the "Fit Check!" button.
          </Typography>
          <Box
            component="img"
            src={theme.palette.mode === "light" ? "/assets/step3-light.png" : "/assets/step3-dark.png"}
            alt="Step 3 - Click Fit Check button"
            sx={{
              width: "100%",
              maxWidth: "500px",
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "grey.300",
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default FitCheckHome;
